<template>
  <div id="bank-information-page">
    <!-- Page Header -->
    <v-card v-if="isStandalone" elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <v-container fluid class="header-container">
          <v-row align="center" justify="space-between" class="ma-0">
            <!-- Title Section - Left -->
            <v-col cols="12" md="8" class="d-flex align-center gap-3">
              <div class="header-icon-box">
                <v-icon size="32" class="icon-white">mdi-bank</v-icon>
              </div>
              <div class="header-title-bar">
                <h1 class="header-title">Bank Information</h1>
                <p class="header-subtitle">Manage your financial accounts</p>
              </div>
            </v-col>

            <!-- Action Button - Right -->
            <v-col cols="12" md="4" class="d-flex justify-end gap-2">
              <v-btn
                v-if="ResultData.length > 0"
                color="white"
                outlined
                rounded
                class="action-btn"
                @click.prevent="updatePrompt = true"
              >
                <v-icon left small>mdi-pencil</v-icon>
                Update Info
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card>

    <!-- Content Container -->
    <v-card-text :class="isStandalone ? 'page-content' : 'page-content--embedded'">
      <v-container fluid :class="isStandalone ? 'pa-0' : 'pa-0 embedded-container'">
        <!-- Bank Cards Grid -->
        <v-row v-if="ResultData.length > 0" :class="isStandalone ? 'mb-6' : 'mb-0'">
          <v-col
            v-for="(item, index) in ResultData"
            :key="item.LomBankAccountId"
            cols="12"
            :lg="isStandalone ? 6 : 12"
            :md="isStandalone ? 6 : 12"
            sm="12"
            class="mb-4"
          >
            <v-card class="bank-info-card" elevation="8">
              <!-- Card Header -->
              <div class="card-header card-header--bank">
                <div class="card-header-icon">
                  <v-icon size="24" class="icon-white">mdi-bank-outline</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">{{ item.BankName }}</h3>
                  <p class="card-subtitle">Account {{ index + 1 }}</p>
                </div>
              </div>

              <!-- Card Body -->
              <v-card-text class="card-body">
                <!-- Account Name Row -->
                <div class="info-row">
                  <div class="info-label">
                    <v-icon small class="label-icon">mdi-account-box</v-icon>
                    Account Name
                  </div>
                  <div class="info-value">{{ item.LomBankAccountName }}</div>
                </div>

                <!-- Account Number Row -->
                <div class="info-row">
                  <div class="info-label">
                    <v-icon small class="label-icon">mdi-credit-card</v-icon>
                    Account Number
                  </div>
                  <div class="info-value account-number">{{ item.LomBankAccountNumber }}</div>
                </div>

                <!-- Branch Name Row -->
                <div class="info-row">
                  <div class="info-label">
                    <v-icon small class="label-icon">mdi-map-marker</v-icon>
                    Branch Name
                  </div>
                  <div class="info-value">{{ item.BranchName }}</div>
                </div>

                <!-- IFSC Code Row -->
                <div class="info-row">
                  <div class="info-label">
                    <v-icon small class="label-icon">mdi-code-string</v-icon>
                    IFSC Code
                  </div>
                  <div class="info-value ifsc-code">{{ item.LomBankAccountIfscCode }}</div>
                </div>

                <!-- Action Button -->
                <div class="card-actions mt-4">
                  <v-btn
                    small
                    color="white"
                    class="edit-btn"
                    @click.prevent="updatePrompt = true"
                  >
                    <v-icon left x-small>mdi-pencil</v-icon>
                    Edit
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-row v-else align="center" justify="center" :class="isStandalone ? 'min-height-300' : 'min-height-embedded'">
          <v-col :cols="isStandalone ? 12 : 12" :md="isStandalone ? 8 : 12" :lg="isStandalone ? 6 : 12" class="text-center">
            <v-card :class="isStandalone ? 'empty-state-card' : 'empty-state-card--embedded'" elevation="8">
              <v-card-text :class="isStandalone ? 'pa-12' : 'pa-6'">
                <div class="empty-state-icon" :class="isStandalone ? '' : 'icon--small'">
                  <v-icon :size="isStandalone ? 80 : 50" color="#EDBE38">mdi-bank</v-icon>
                </div>
                <h2 class="empty-state-title" :class="isStandalone ? 'mt-6' : 'mt-3'">No Bank Accounts Added</h2>
                <p class="empty-state-description mt-2">
                  Start by adding your bank account information.
                </p>
                <v-btn
                  :large="isStandalone"
                  :small="!isStandalone"
                  class="add-btn"
                  :class="isStandalone ? 'mt-6' : 'mt-3'"
                  @click.prevent="updatePrompt = true"
                >
                  <v-icon left :small="!isStandalone">mdi-plus</v-icon>
                  Add Bank Account
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <!-- Update Dialog -->
    <v-dialog v-model="updatePrompt" persistent max-width="70%">
      <bank-information-update
        v-if="updatePrompt"
        @hideupdatePrompt="hideupdatePrompt"
      ></bank-information-update>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import BankInformationUpdate from "./BankInformationUpdate.vue";

export default {
  mixins: [common],
  components: {
    BankInformationUpdate,
  },
  props: {
    isStandalone: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      ResultData: [],
      LomId: "",
      updatePrompt: false,
      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      CurrentYearId: "",
    };
  },
  mounted() { },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomId = LomId;

        this.pageData();
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "jci_india_lom",
        Action: "list_2",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getBankDetails();
    },
    hideupdatePrompt() {
      console.log("hideupdatePrompt called");
      this.updatePrompt = false;
      this.getBankDetails();
    },
    getBankDetails() {
      console.log("getBankDetails called");
      this.LoadingFlag = false;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/lom-bank-details/lists";
      var upload = {
        UserInterface: 1,
        Lom: this.LomId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);
          console.log({ records });

          if (flag == 1) {
            // thisIns.toast("success", output);
            thisIns.ResultData = records.TableData;

            console.log("ResultData=" + JSON.stringify(thisIns.ResultData));
          } else {
            // thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
        });
    },
  },
  mounted() { },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss" scoped>
// ============================================
// COLOR PALETTE - JCI APPROVED
// ============================================
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$bank-color: $aqua;

$light-bg: #f8fafc;
$bg-light: #f1f5f9;
$border-color: #e2e8f0;

// ============================================
// ANIMATIONS
// ============================================
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-12px);
  }
}

#bank-information-page {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 30px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: float 8s ease-in-out infinite;
    }
  }

  .header-backdrop-effect {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  .header-container {
    padding: 20px 0 !important;
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;

    .icon-white {
      color: $text-white !important;
    }
  }

  .header-title-bar {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
    margin: 0;
  }

  .gap-3 {
    gap: 1rem !important;
  }

  .action-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: rgba(255, 255, 255, 0.25) !important;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    }
  }

  // ============================================
  // PAGE CONTENT
  // ============================================
  .page-content {
    padding: 0;
  }

  // ============================================
  // BANK CARD
  // ============================================
  .bank-info-card {
    border-radius: 18px !important;
    border: 2px solid rgba(0, 151, 215, 0.15) !important;
    box-shadow: 0 12px 40px rgba(0, 151, 215, 0.15),
      0 0 30px rgba(0, 151, 215, 0.08);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    animation: slideInUp 0.6s ease-out;
    background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
        circle at 20% 50%,
        rgba(0, 151, 215, 0.05) 0%,
        transparent 50%
      );
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 60px rgba(0, 151, 215, 0.2),
        0 0 40px rgba(0, 151, 215, 0.15);
      border-color: rgba(0, 151, 215, 0.3) !important;
    }
  }

  // ============================================
  // CARD HEADER
  // ============================================
  .card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 16px 16px 0 0;
    background: linear-gradient(135deg, $aqua 0%, darken($aqua, 10%) 100%);
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: float 6s ease-in-out infinite;
    }
  }

  .card-header--bank {
    background: linear-gradient(135deg, $aqua 0%, lighten($aqua, 10%) 100%);
  }

  .card-header-icon {
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid rgba(255, 255, 255, 0.3);
    flex-shrink: 0;
    position: relative;
    z-index: 1;

    .v-icon {
      color: $text-white !important;
    }
  }

  .card-header-content {
    flex: 1;
    position: relative;
    z-index: 1;
  }

  .card-title {
    font-size: 15px;
    font-weight: 700;
    color: $text-white;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .card-subtitle {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.9);
    margin: 2px 0 0 0;
    font-weight: 500;
    opacity: 0.95;
  }

  // ============================================
  // CARD BODY
  // ============================================
  .card-body {
    padding: 12px !important;
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .info-row {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    background: linear-gradient(
      135deg,
      rgba(0, 151, 215, 0.04) 0%,
      rgba(0, 151, 215, 0.01) 100%
    );
    border-radius: 6px;
    border-left: 2px solid $aqua;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(0, 151, 215, 0.07) 0%,
        rgba(0, 151, 215, 0.03) 100%
      );
      transform: translateX(2px);
    }
  }

  .info-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 9px;
    font-weight: 700;
    color: $text-90-black;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    white-space: nowrap;
    grid-column: 1;

    .label-icon {
      color: $aqua !important;
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .info-value {
    flex: 1;
    font-size: 12px;
    font-weight: 600;
    color: $text-off-black;
    word-break: break-word;
    line-height: 1.3;
    grid-column: 2;
    text-align: right;

    &.account-number,
    &.ifsc-code {
      font-family: "Courier New", monospace;
      letter-spacing: 0.5px;
      background: rgba(0, 151, 215, 0.06);
      padding: 3px 6px;
      border-radius: 4px;
      font-weight: 700;
      font-size: 10px;
      text-align: right;
    }
  }

  // ============================================
  // CARD ACTIONS
  // ============================================
  .card-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 6px !important;
  }

  .edit-btn {
    background: linear-gradient(135deg, $aqua 0%, darken($aqua, 10%) 100%) !important;
    color: white !important;
    font-weight: 600 !important;
    padding: 0 12px !important;
    height: 28px !important;
    font-size: 11px !important;
    border-radius: 6px !important;
    transition: all 0.3s ease !important;

    &:hover {
      box-shadow: 0 6px 20px rgba(0, 151, 215, 0.3);
      transform: translateY(-2px);
    }
  }

  // ============================================
  // EMBEDDED MODE STYLING
  // ============================================
  .page-content--embedded {
    padding: 0 !important;
  }

  .embedded-container {
    padding: 0 !important;
  }

  .min-height-embedded {
    min-height: auto;
    padding: 0;
  }

  // ============================================
  // EMPTY STATE
  // ============================================
  .min-height-300 {
    min-height: 300px;
  }

  .empty-state-card {
    border-radius: 18px !important;
    border: 2px dashed $gold !important;
    background: linear-gradient(135deg, #fffef0 0%, #fffff5 100%) !important;
    animation: slideInUp 0.6s ease-out;

    &--embedded {
      border: 1px dashed $gold !important;
      background: linear-gradient(135deg, #fffef9 0%, #fffff7 100%) !important;
    }

    .empty-state-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 120px;
      height: 120px;
      background: linear-gradient(135deg, rgba(237, 190, 56, 0.1) 0%, rgba(237, 190, 56, 0.05) 100%);
      border-radius: 50%;
      margin: 0 auto;

      &.icon--small {
        width: 80px;
        height: 80px;
      }
    }

    .empty-state-title {
      font-size: 24px;
      font-weight: 700;
      color: $text-off-black;
      margin: 0;
    }

    .empty-state-description {
      font-size: 14px;
      color: $text-90-black;
      max-width: 400px;
      margin: 0 auto;
    }

    .add-btn {
      background: linear-gradient(135deg, $gold 0%, darken($gold, 15%) 100%) !important;
      color: white !important;
      font-weight: 700 !important;
      text-transform: none !important;
      border-radius: 10px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

      &:hover {
        box-shadow: 0 8px 24px rgba(237, 190, 56, 0.3);
        transform: translateY(-3px);
      }
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    .header-card {
      margin-bottom: 20px !important;
    }

    .header-title {
      font-size: 28px !important;
    }

    .header-subtitle {
      font-size: 14px !important;
    }

    .info-label {
      min-width: 110px !important;
      font-size: 11px !important;
    }

    .info-value {
      font-size: 13px !important;
    }
  }

  @media (max-width: 600px) {
    padding: 15px;

    .header-card {
      margin-bottom: 15px !important;
    }

    .header-title {
      font-size: 24px !important;
    }

    .header-subtitle {
      font-size: 12px !important;
    }

    .bank-info-card {
      margin-bottom: 12px !important;
    }

    .card-title {
      font-size: 16px !important;
    }

    .card-subtitle {
      font-size: 12px !important;
    }

    .info-row {
      flex-direction: column;
      gap: 8px;
    }

    .info-label {
      min-width: unset !important;
    }

    .card-body {
      padding: 16px !important;
      gap: 12px;
    }
  }
}
</style>